import React, { useState } from 'react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Clock, Calendar as CalendarIcon, Check, User, Phone } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AppointmentCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [clientName, setClientName] = useState('');
  const [step, setStep] = useState(1); // 1: date, 2: time, 3: info, 4: confirmation

  // Créneaux horaires disponibles
  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ];

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setStep(2);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(3); // Go to info step
  };

  const handleInfoSubmit = () => {
    if (clientName && phoneNumber) {
      setStep(4); // Go to confirmation
    }
  };

  const handleConfirm = async () => {
    const appointmentData = {
      client_name: clientName,
      phone_number: phoneNumber,
      date: selectedDate?.toISOString().split('T')[0], // Format YYYY-MM-DD
      time: selectedTime
    };
    
    try {
      const response = await fetch(`${API_URL}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData)
      });

      if (response.ok) {
        toast.success(`Rendez-vous confirmé pour ${clientName} le ${selectedDate?.toLocaleDateString('fr-FR')} à ${selectedTime}`);
        
        // Reset after success
        setTimeout(() => {
          setSelectedDate(null);
          setSelectedTime(null);
          setPhoneNumber('');
          setClientName('');
          setStep(1);
        }, 2000);
      } else {
        toast.error("Impossible de confirmer le rendez-vous. Veuillez réessayer.");
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error("Une erreur s'est produite. Veuillez réessayer.");
    }
  };

  const handleReset = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setPhoneNumber('');
    setClientName('');
    setStep(1);
  };

  // Désactiver les dates passées
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <Card className="bg-light/5 backdrop-blur-sm border-gold/20 shadow-luxury">
      <CardHeader>
        <CardTitle className="text-2xl font-playfair text-white flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 text-gold" />
          Prendre Rendez-vous
        </CardTitle>
        <CardDescription className="text-gray-400">
          Sélectionnez une date et un créneau horaire pour votre consultation
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Step 1: Sélection de la date */}
        {step >= 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              {step > 1 && <Check className="w-5 h-5 text-neon" />}
              1. Choisissez une date
            </h3>
            
            {step === 1 ? (
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={isDateDisabled}
                  className="rounded-md border border-gold/20 bg-white/5 p-4"
                  classNames={{
                    months: "text-white",
                    month: "space-y-4",
                    caption: "text-white font-semibold",
                    caption_label: "text-lg",
                    nav_button: "text-white hover:text-gold",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex",
                    head_cell: "text-gray-400 rounded-md w-9 font-normal text-[0.8rem]",
                    row: "flex w-full mt-2",
                    cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-gold/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                    day: "h-9 w-9 p-0 font-normal text-white hover:bg-gold/30 hover:text-white rounded-md transition-colors",
                    day_selected: "bg-gold text-dark hover:bg-gold hover:text-dark focus:bg-gold focus:text-dark font-semibold",
                    day_today: "bg-white/10 text-white font-semibold",
                    day_disabled: "text-gray-600 opacity-50 cursor-not-allowed",
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-4 bg-white/5 p-4 rounded-md border border-gold/20">
                <CalendarIcon className="w-5 h-5 text-gold" />
                <span className="text-white font-medium">
                  {selectedDate?.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="ml-auto text-gold hover:text-gold-light"
                >
                  Modifier
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Sélection de l'heure */}
        {step >= 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              {step > 2 && <Check className="w-5 h-5 text-neon" />}
              2. Choisissez un créneau horaire
            </h3>
            
            {step === 2 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {timeSlots.map((time) => (
                  <Button
                    key={time}
                    variant={selectedTime === time ? "default" : "outline"}
                    onClick={() => handleTimeSelect(time)}
                    className={`
                      ${selectedTime === time 
                        ? 'bg-gold hover:bg-gold-light text-dark border-gold' 
                        : 'bg-white/5 hover:bg-white/10 text-white border-gold/30'}
                      transition-all duration-300
                    `}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {time}
                  </Button>
                ))}
              </div>
            ) : step > 2 && (
              <div className="flex items-center gap-4 bg-white/5 p-4 rounded-md border border-gold/20">
                <Clock className="w-5 h-5 text-gold" />
                <span className="text-white font-medium">{selectedTime}</span>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Informations client */}
        {step >= 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              {step > 3 && <Check className="w-5 h-5 text-neon" />}
              3. Vos coordonnées
            </h3>
            
            {step === 3 ? (
              <div className="space-y-4 bg-white/5 p-6 rounded-md border border-gold/20">
                <div>
                  <Label htmlFor="clientName" className="text-white mb-2 block flex items-center gap-2">
                    <User className="w-4 h-4 text-gold" />
                    Nom complet
                  </Label>
                  <Input
                    id="clientName"
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Votre nom"
                    className="bg-white/5 border-gold/30 text-white focus:border-gold focus:ring-gold"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="phoneNumber" className="text-white mb-2 block flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gold" />
                    Numéro de téléphone
                  </Label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="06 XX XX XX XX"
                    className="bg-white/5 border-gold/30 text-white focus:border-gold focus:ring-gold"
                    required
                  />
                </div>
                
                <Button
                  onClick={handleInfoSubmit}
                  disabled={!clientName || !phoneNumber}
                  className="w-full bg-gold hover:bg-gold-light text-dark font-semibold transition-all duration-300"
                >
                  Continuer
                </Button>
              </div>
            ) : step > 3 && (
              <div className="bg-white/5 p-4 rounded-md border border-gold/20 space-y-2">
                <div className="flex items-center gap-3 text-white">
                  <User className="w-4 h-4 text-gold" />
                  <span className="font-medium">{clientName}</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <Phone className="w-4 h-4 text-gold" />
                  <span className="font-medium">{phoneNumber}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="text-gold hover:text-gold-light mt-2"
                >
                  Modifier
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">4. Confirmer le rendez-vous</h3>
            
            <div className="bg-white/5 p-6 rounded-md border border-gold/20 space-y-4">
              <div className="text-center border-b border-gold/20 pb-4">
                <p className="text-gray-400 mb-2">Votre rendez-vous</p>
                <p className="text-2xl font-playfair text-white mb-1">
                  {selectedDate?.toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
                <p className="text-xl text-gold font-semibold">à {selectedTime}</p>
              </div>
              
              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-3 text-white">
                  <User className="w-5 h-5 text-gold" />
                  <span className="font-medium">{clientName}</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <Phone className="w-5 h-5 text-gold" />
                  <span className="font-medium">{phoneNumber}</span>
                </div>
              </div>
              
              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="flex-1 border-gold/30 text-white hover:bg-white/10"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleConfirm}
                  className="flex-1 bg-gold hover:bg-gold-light text-dark font-semibold shadow-gold"
                >
                  Confirmer
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
